/* eslint-disable react/prop-types */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { canadianPayrollAPI } from '../lib/api';
import { useAuth } from './AuthContext';

/**
 * CanadianPayrollContext
 *
 * Fetches `/canadian-payroll/eligible` once after auth resolves.
 * Exposes:
 *   - caEnabled            — true if the tenant has any CA presence
 *   - hasCaLocations       — has at least one CA location
 *   - hasNonCaLocations    — multi-country tenant
 *   - caLocationIds        — list of CA location ids
 *   - refresh()            — manual refresh
 *
 * Backend remains the source of truth; this context only hides UI.
 * Per-employee gating is still enforced server-side (mixed-country tenants).
 */
const CanadianPayrollContext = createContext({
  caEnabled: false,
  hasCaLocations: false,
  hasNonCaLocations: false,
  caLocationIds: [],
  loading: true,
  refresh: () => {},
});

export function CanadianPayrollProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    caEnabled: false,
    hasCaLocations: false,
    hasNonCaLocations: false,
    caLocationIds: [],
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!user) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const r = await canadianPayrollAPI.eligible();
      setState({
        caEnabled: !!r.data?.eligible,
        hasCaLocations: !!r.data?.has_ca_locations,
        hasNonCaLocations: !!r.data?.has_non_ca_locations,
        caLocationIds: r.data?.ca_location_ids || [],
        loading: false,
      });
    } catch {
      setState({
        caEnabled: false, hasCaLocations: false,
        hasNonCaLocations: false, caLocationIds: [], loading: false,
      });
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <CanadianPayrollContext.Provider value={{ ...state, refresh }}>
      {children}
    </CanadianPayrollContext.Provider>
  );
}

export const useCanadianPayroll = () => useContext(CanadianPayrollContext);
